import * as React from 'react';
import { LedgerReferenceType, PTOEntryType, PTOPlanType } from './types';
import DoublePanel from '../../components/common/DoublePanel';
import ManageDetail from './ManageDetail';
import { CSVLink } from 'react-csv';
import { Modal } from 'react-bootstrap';
import IconBuilder from 'src/utilities/iconBuilder/IconBuilder';
import Loader from '../../components/common/Loader';
import './PTOAdminTable.css';

interface IProps {
    currentProfitCenter: { id: string, displayname: string } | null;
    fromToDate: { from: string, to: string };
    includeInactive: boolean;
    reference: { states: Array<any>, profitCenters: Array<any>, departments: Array<any>, ptoPlans: Array<PTOPlanType> };
    updateTotal: any;
    ptoData: {
        data: Array<PTOEntryType>;
        ledgerEntryTypes: Array<any>;
        ledgerStatusTypes: Array<any>;
    };
    updateTable: any;
    startVertical: boolean;
    displayZeros: boolean;
    setDisplayZeros: (displayZeros: boolean) => void;
    expandDetail: boolean;
    setExpandDetail: (expandDetail: boolean) => void;
    employeeCount: number;
    setEmployeeCount: (employeeCount: number) => void;
    filteredEmployeeCount: number;
    setFilteredEmployeeCount: (filteredEmployeeCount: number) => void;
    showOnlyPending: boolean;

}

const PTOAdminTable: React.FC<IProps> = (props: IProps) => {
    const { fromToDate, includeInactive, reference, ptoData,
        displayZeros, expandDetail, setExpandDetail, filteredEmployeeCount,
        setFilteredEmployeeCount, showOnlyPending } = props;
    const tableRef: any = React.useRef();
    const [tableData, setTableData] = React.useState([] as Array<PTOEntryType>);
    //r-18-lint-dj
    //eslint-disable-next-line
    const [stableTableData, setStableTableData] = React.useState([] as Array<PTOEntryType>);
    const [ledgerReference, setLedgerReference] = React.useState({} as {
        ledgerEntryTypes: Array<LedgerReferenceType>;
        ledgerStatusTypes: Array<LedgerReferenceType>;
    });
    const [showDetailAsModal, setShowDetailAsModal] = React.useState(false);
    //r-18-lint-dj
    //eslint-disable-next-line
    const [vertical, setVertical] = React.useState(props.startVertical);
    const componentMounted = React.useRef(true);

    const getData = () => {
        if (ptoData) {
            tableRef.current?.toggleLoading(true);
            setTimeout(() => {
                const { data, ledgerStatusTypes, ledgerEntryTypes } = ptoData;
                //cgm032422 filtering out all data without pending requests 
                //cgm042022 was getting a warning about a memory leak.  This checks if component is mounted before setting state.    
                if (componentMounted.current) {
                    setTableData(data);
                    setStableTableData(data);
                    setLedgerReference({
                        ledgerEntryTypes,
                        ledgerStatusTypes
                    })
                }
                // getReferenceData();
                tableRef.current?.toggleLoading(false);
                return () => {
                    componentMounted.current = false;
                }
            }, 1000)
        }
    }

    const refreshTable = () => {
        const { data, ledgerStatusTypes, ledgerEntryTypes } = ptoData;
        setTableData(data);
        setLedgerReference({
            ledgerEntryTypes,
            ledgerStatusTypes
        })
    }

    const toggleIsModal = () => {
        setShowDetailAsModal(!showDetailAsModal);
        setExpandDetail(!expandDetail);
    }

    // keep a ref with the latest values so DetailPanel can read them without being recreated every render
    const detailPanelRef = React.useRef({ showDetailAsModal, ledgerReference, reference, updateTable: props.updateTable, toggleIsModal });
    detailPanelRef.current = { showDetailAsModal, ledgerReference, reference, updateTable: props.updateTable, toggleIsModal };

    // empty [] means this function is created once and never replaced
    // DoublePanel sees the same function ref every render, so it never unmounts/remounts ManageDetail during re-renders 
    const StableDetailPanel = React.useCallback(({ details, handleClose, toggleVertical, isVertical }: any) => {
        const { showDetailAsModal, ledgerReference, reference, updateTable, toggleIsModal } = detailPanelRef.current;
        if (showDetailAsModal) {
            return (
                <Modal show={true} dialogClassName="pto-admin-modal" centered>
                    <Modal.Body>
                        <ManageDetail
                            ledgerReference={ledgerReference}
                            reference={reference}
                            handleClose={handleClose}
                            details={details}
                            updateTable={updateTable}
                            toggleModal={toggleIsModal}
                            isModal={showDetailAsModal}
                            toggleVertical={toggleVertical}
                            isVertical={isVertical}
                        />
                    </Modal.Body>
                </Modal>
            );
        }
        return (
            <ManageDetail
                ledgerReference={ledgerReference}
                reference={reference}
                handleClose={handleClose}
                details={details}
                updateTable={updateTable}
                toggleModal={toggleIsModal}
                isModal={showDetailAsModal}
                toggleVertical={toggleVertical}
                isVertical={isVertical}
            />
        );
    }, []);

    React.useEffect(() => {
                getData();
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [fromToDate, includeInactive, displayZeros])

    React.useEffect(() => {
        refreshTable();
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [filteredEmployeeCount])

    React.useEffect(() => {
        setShowDetailAsModal(expandDetail);
        refreshTable();
        //r-18-lint-dj
        //eslint-disable-next-line
    }, [ptoData, expandDetail])

    // const toggleVertical = () => {
    //     setVertical(!vertical);
    // }

    //cgm for  tracking number of filtered employees count vs total
    let filteredData = tableData.filter(element => element.PendingHours < 0);
    //cgm 042022 =>> fixing memeory leak error from react
    React.useEffect(() => {
        showOnlyPending ?
            setFilteredEmployeeCount(filteredData.length) :
            setFilteredEmployeeCount(tableData.length);
    }, [showOnlyPending, tableData])
    if (tableRef.current?.toggleLoading === true) {
        return (
            <Loader title="Loading Employee Information..." />
        )
    }


    const ptoPlanExpander = (index: number) => {
        let plan = document.getElementById(`pto-admin-table-pto-plan-${index}`);
        plan?.classList.remove('text-truncate');
        plan?.classList.remove('overflow-hidden');
    }

    const ptoPlanShrinker = (index: number) => {
        let plan = document.getElementById(`pto-admin-table-pto-plan-${index}`);
        plan?.classList.add('text-truncate');
        plan?.classList.add('overflow-hidden');
    }

    return <div className="employee-pto-list d-inline-flex pto-details shadow m-1" style={{ height: 800, }}>
        <DoublePanel
            turnIntoModal={false}
            startVertical={vertical}   //for toggling the double panel between horiz and vertical         
            manual
            url=""
            ref={tableRef}
            keyField="UserId"
            //cgm032422 employees are filtered out of the list if they don't have a pending request
            data={showOnlyPending ?
                tableData.filter((element) => element.PendingHours < 0).sort((a: PTOEntryType, b: PTOEntryType) => a.DisplayName.localeCompare(b.DisplayName)) :
                tableData.sort((a: PTOEntryType, b: PTOEntryType) => a.DisplayName.localeCompare(b.DisplayName))
            }
            columns={[
                {
                    dataField: "DisplayName",
                    text: "Employee",
                    isDummyField: true,
                    sort: true,
                    defaultSorted: "desc",
                    filterValue: (cell: any, row: any) => row.DisplayName,
                    formatter: (cell: any, row: any) => {
                        return <div className="d-flex flex-column">
                            <h6 className="font-weight-bold">{row.DisplayName}</h6>
                        </div>
                    }
                },
                { dataField: "UserNumber", text: "Employee #", sort: true },
                {//PTO PLan
                    dataField: "PTO Plan",
                    text: "PTO Plan",
                    headerFormatter: (column: any) => (<div className=" d-flex align-items-center justify-content-between text-truncate">
                        <span>{column.text}</span>
                    </div>),
                    formatter: (cell: any, row: any, index: any) => {
                        return <div id={`pto-admin-table-pto-plan-${index}`} className="pto-plan text-truncate" onMouseEnter={() => ptoPlanExpander(index)} onMouseLeave={() => ptoPlanShrinker(index)} >
                            {reference.ptoPlans.find((plan: PTOPlanType) => plan.plan.PTOPlanId === row.PTOPlanId)?.plan.PTOPlanName || "N/A"}
                        </div>
                    }
                },
                {
                    dataField: "CurrentBalance",
                    text: "Current Balance",
                    headerFormatter: (column: any) => (<span>{column.text} <span className="text-secondary">&#40;hours&#41;</span></span>),
                    formatter: (cell: any, row: any, extraData: any) => {
                        let value = cell;
                        return (
                            <div>
                                {displayZeros ? value.toFixed(2) : value === 0 ? "-" : value.toFixed(2)}
                            </div>
                        )
                    }
                },
                {
                    dataField: "UsedYTD",
                    text: "Used YTD",
                    headerFormatter: (column: any) => (<span>{column.text} <span className="text-secondary">&#40;hours&#41;</span></span>),
                    formatter: (cell: any, row: any) => {
                        let value = cell >= 0 ? cell : -cell
                        return (
                            <div>
                                {displayZeros ? value.toFixed(2) : value === 0 ? "-" : value.toFixed(2)}
                            </div>
                        )
                    }
                },
                {
                    dataField: "PendingHours",
                    text: "Requested Hours",
                    headerFormatter: (cell: any, column: any) => (
                        <div className="d-flex flex-shrink-1 flex-row align-items-end justify-content-between">
                            <span>Requested <span className="text-secondary">&#40;hours&#41;</span></span>
                            <CSVLink
                                className="action-text d-flex align-items-center justify-content-center px-1 ml-auto"
                                style={{ height: 45, width: 45, marginRight: 20 }}
                                filename={"pto_administration.csv"}
                                data={tableData}
                                headers={[
                                    { label: "User ID", key: "UserId" },
                                    { label: "Supervisor Id", key: "SupervisorId" },
                                    { label: "Inactive", key: "Inactive" },
                                    { label: "Display Name", key: "DisplayName" },
                                    { label: "Current Balance", key: "CurrentBalance" },
                                    { label: "Pending Hours", key: "PendingHours" },
                                    { label: "Used YTD", key: "UsedYTD" },
                                    { label: "PTO Plan", key: "PTOPlan" }
                                ] as Array<any>}
                                target="_blank"
                                title="Download CSV"
                            >
                                <IconBuilder extension="csv" size={32}></IconBuilder>
                            </CSVLink>
                        </div>
                    ),
                    formatter: (cell: any, row: any) => {
                        let value = cell >= 0 ? cell : -cell;
                        return (<div className={`${value > 0 ? "request-warning" : ""}`}><span >
                            {displayZeros ? value.toFixed(2) : value === 0 ? "-" : value.toFixed(2)}
                        </span></div>)

                    }
                },

            ]}
            DetailPanel={StableDetailPanel}
        />
    </div>
}

export default PTOAdminTable;